import apiService from "../services/api";

import { debugLog } from "./debugLog";

/**
 * Utility for tracking and managing watch progress for movies and series
 * Based on Android implementation in WatchProgressUtils.kt
 */

class WatchProgressUtils {
  constructor() {
    this.hasProgressChangedFlag = false;
    this.progressTrackingInterval = null;
    this.lastSavedProgress = null;
  }

  /**
   * Enriches a list of media items with their watch progress data.
   * This adds the progress percentage to each item for display in carousels.
   *
   * @param {Array} items - Array of media items (movies/series/livetv)
   * @returns {Array} Items with progress data added
   */
  enrichItemsWithProgress(items) {
    if (!Array.isArray(items) || items.length === 0) {
      return items;
    }

    return items.map((item) => {
      // Determine media ID and type
      const mediaId =
        item.mediaId?.toString() ||
        item.id?.toString() ||
        item.stream_id?.toString();
      const mediaType = item.mediaType || item.type || "movie";

      // If already has meaningful progress (> 0), return as-is
      // We still check localStorage if progress is 0 since we might have newer data
      if (
        item.progress !== undefined &&
        item.progress !== null &&
        item.progress > 0
      ) {
        return item;
      }

      // Get progress for this item
      if (mediaId) {
        const progressData = this.getValidatedProgress(mediaId, mediaType);

        if (progressData.isValid) {
          return {
            ...item,
            progress: progressData.progressPercent || 0,
            watchProgress: progressData.progress,
            watchDuration: progressData.duration,
            watched: progressData.watched,
          };
        }
      }

      return item;
    });
  }

  /**
   * Gets the watch progress for a media item and validates it.
   * Progress is only considered valid if:
   * - Position is greater than 10 seconds
   * - Duration is greater than 0
   *
   * @param {string} mediaId - The media ID to check progress for
   * @param {string} mediaType - The type of media (movie/tv)
   * @returns {object} Progress object with validation info
   */
  getValidatedProgress(mediaId, mediaType) {
    const storageKey = "webPlayer_recentlyWatchedProgress";
    const progressData = localStorage.getItem(storageKey);

    debugLog("getValidatedProgress called with:", { mediaId, mediaType });

    if (!progressData) {
      debugLog("No progress data in localStorage");

      return {
        progress: 0,
        duration: 0,
        watched: false,
        isValid: false,
        progressPercent: 0,
        mediaId,
        mediaType,
      };
    }

    try {
      const progressList = JSON.parse(progressData);

      debugLog("Looking for mediaId:", mediaId, "mediaType:", mediaType);
      debugLog(
        "First 3 items in localStorage (full structure):",
        progressList.slice(0, 3)
      );

      // Normalize mediaType for comparison (tv/series are equivalent)
      const normalizeMediaType = (type) => {
        if (type === "tv" || type === "series" || type === "episode") {
          return "series";
        }

        return type || "movie";
      };

      const normalizedSearchType = normalizeMediaType(mediaType);
      const searchMediaId = String(mediaId);

      // For series, localStorage stores compound IDs like "seriesId-episodeId"
      // We need to match if the stored ID starts with the seriesId
      const serverProgress = progressList.find((item) => {
        const storedMediaId = String(item.mediaId);
        const storedType = normalizeMediaType(item.mediaType);

        // Exact match
        if (
          storedMediaId === searchMediaId &&
          storedType === normalizedSearchType
        ) {
          return true;
        }

        // For series: check if stored ID starts with "searchId-" (compound format)
        if (normalizedSearchType === "series" && storedType === "series") {
          if (storedMediaId.startsWith(`${searchMediaId}-`)) {
            return true;
          }
        }

        return false;
      });

      debugLog(
        "Search:",
        searchMediaId,
        normalizedSearchType,
        "Found:",
        serverProgress?.mediaId
      );

      debugLog("Found progress for media:", serverProgress);

      if (
        serverProgress?.progress != null &&
        serverProgress?.duration != null
      ) {
        return serverProgress;
      }

      debugLog("No valid progress found for this media");

      return {
        progress: 0,
        duration: 0,
        watched: false,
        isValid: false,
        progressPercent: 0,
        mediaId,
        mediaType,
      };
    } catch (error) {
      console.error("Error parsing progress data:", error);

      return {
        progress: 0,
        duration: 0,
        watched: false,
        isValid: false,
        progressPercent: 0,
        mediaId,
        mediaType,
      };
    }
  }

  /**
   * Saves watch progress for a media item both locally and to the server.
   * Body shape mirrors Samsung's syncProgressToApi() (lunova-samsung
   * src/js/player.js:4045-4059) so the server can populate /v2/home →
   * continueWatching with title + poster.
   *
   * @param {string} mediaId
   * @param {string} mediaType - movie | tv
   * @param {number} progress - current position in ms
   * @param {number} duration - total duration in ms
   * @param {boolean} watched
   * @param {object} [meta] - title, posterUrl, thumbnailUrl, streamUrl, episodeId, episodeInfo
   */
  async saveProgress(
    mediaId,
    mediaType,
    progress,
    duration,
    watched = false,
    meta = {}
  ) {
    const token = localStorage.getItem("webPlayerToken");

    if (!token) {
      debugLog("No auth token, skipping progress save");

      return;
    }

    if (!mediaId || !mediaType || duration <= 0) {
      console.warn("Invalid progress parameters:", {
        mediaId,
        mediaType,
        progress,
        duration,
      });

      return;
    }

    this.hasProgressChangedFlag = true;

    const isValid = progress > 10000 && duration > 0;
    const progressPercent = isValid
      ? Math.min(Math.max(Math.round((progress / duration) * 100), 0), 100)
      : 0;

    this.updateLocalCache(
      mediaId,
      mediaType,
      progress,
      duration,
      watched,
      meta
    );

    try {
      await apiService.webPlayerUpdateRecentlyWatched({
        mediaType,
        mediaId: String(mediaId),
        metadata: {
          progress,
          duration,
          isValid,
          progressPercent,
          mediaId: String(mediaId),
          mediaType,
          watched,
          episodeId: meta.episodeId ?? null,
          streamUrl: meta.streamUrl ?? null,
          episodeInfo: meta.episodeInfo ?? null,
          title: meta.title ?? null,
          posterUrl: meta.posterUrl ?? null,
          thumbnailUrl: meta.thumbnailUrl ?? null,
        },
      });

      debugLog("Progress saved successfully:", {
        mediaId,
        mediaType,
        progressPercent,
        watched,
      });
    } catch (error) {
      console.error("Error saving progress to API:", error);
    }
  }

  /**
   * Clears the watch progress for a media item.
   *
   * @param {string} mediaId - The media ID to clear progress for
   * @param {string} mediaType - The type of media (movie/tv)
   */
  async clearProgress(mediaId, mediaType) {
    await this.saveProgress(mediaId, mediaType, 0, 0, false);
  }

  /**
   * Updates the local cache of recently watched progress.
   * This keeps the cache in sync with the server.
   *
   * @private
   */
  updateLocalCache(mediaId, mediaType, progress, duration, watched, meta = {}) {
    const storageKey = "webPlayer_recentlyWatchedProgress";
    const progressData = localStorage.getItem(storageKey);
    let currentList = [];

    if (progressData) {
      try {
        currentList = JSON.parse(progressData);
      } catch (error) {
        console.error("Error parsing existing progress data:", error);
        currentList = [];
      }
    }

    currentList = currentList.filter(
      (item) => !(item.mediaId === mediaId && item.mediaType === mediaType)
    );

    const isValid = progress > 10000 && duration > 0;
    const progressPercent = isValid
      ? Math.min(Math.max(Math.round((progress / duration) * 100), 0), 100)
      : 0;

    currentList.push({
      progress,
      duration,
      watched,
      isValid,
      progressPercent,
      mediaId,
      mediaType,
      episodeId: meta.episodeId ?? null,
      streamUrl: meta.streamUrl ?? null,
      episodeInfo: meta.episodeInfo ?? null,
      title: meta.title ?? null,
      posterUrl: meta.posterUrl ?? null,
      thumbnailUrl: meta.thumbnailUrl ?? null,
    });

    localStorage.setItem(storageKey, JSON.stringify(currentList));
  }

  /**
   * Checks if watch progress has changed since last check.
   * Resets the flag after checking.
   *
   * @returns {boolean} true if progress has changed, false otherwise
   */
  hasProgressChanged() {
    const changed = this.hasProgressChangedFlag;

    if (changed) {
      this.hasProgressChangedFlag = false;
    }

    return changed;
  }

  /**
   * Starts periodic progress tracking for a video player.
   * Saves progress every 30 seconds while playing.
   *
   * @param {object} player - The Plyr player instance
   * @param {string} mediaId - The media ID
   * @param {string} mediaType - The type of media (movie/tv)
   * @param {object} options - Optional configuration
   */
  startProgressTracking(player, mediaId, mediaType, options = {}, meta = {}) {
    const {
      saveInterval = 30000, // Save every 30 seconds
      minProgress = 10000, // Minimum 10 seconds before saving
    } = options;

    this.stopProgressTracking();

    setTimeout(() => {
      if (player && player.currentTime && player.duration) {
        const progress = Math.floor(player.currentTime * 1000);
        const duration = Math.floor(player.duration * 1000);

        if (progress >= minProgress) {
          const watched = this.isWatched(progress, duration, mediaType);

          this.saveProgress(
            mediaId,
            mediaType,
            progress,
            duration,
            watched,
            meta
          );
        }
      }
    }, minProgress);

    this.progressTrackingInterval = setInterval(() => {
      if (player && player.currentTime && player.duration) {
        const progress = Math.floor(player.currentTime * 1000);
        const duration = Math.floor(player.duration * 1000);

        if (progress >= minProgress) {
          const watched = this.isWatched(progress, duration, mediaType);

          this.saveProgress(
            mediaId,
            mediaType,
            progress,
            duration,
            watched,
            meta
          );
          this.lastSavedProgress = progress;
        }
      }
    }, saveInterval);

    debugLog("Started progress tracking for:", { mediaId, mediaType });
  }

  /**
   * Stops periodic progress tracking.
   */
  stopProgressTracking() {
    if (this.progressTrackingInterval) {
      clearInterval(this.progressTrackingInterval);
      this.progressTrackingInterval = null;
      debugLog("Stopped progress tracking");
    }
  }

  /**
   * Saves current player progress immediately.
   * Called when player is paused or closed.
   *
   * @param {object} player - The Plyr player instance
   * @param {string} mediaId - The media ID
   * @param {string} mediaType - The type of media (movie/tv)
   */
  async savePlayerProgress(player, mediaId, mediaType, meta = {}) {
    if (!player || !player.currentTime || !player.duration) {
      console.warn("Invalid player state for progress save");

      return;
    }

    const progress = Math.floor(player.currentTime * 1000);
    const duration = Math.floor(player.duration * 1000);

    if (progress > 10000) {
      const watched = this.isWatched(progress, duration, mediaType);

      await this.saveProgress(
        mediaId,
        mediaType,
        progress,
        duration,
        watched,
        meta
      );
    }
  }

  /**
   * Samsung parity (lunova-samsung src/js/player.js:4042-4043):
   * movies marked watched at ≥90%, TV at ≥95%.
   */
  isWatched(progress, duration, mediaType) {
    if (!duration || duration <= 0) return false;

    const progressPercent = (progress / duration) * 100;

    if (mediaType === "movie") {
      return progressPercent >= 90;
    } else if (mediaType === "tv") {
      return progressPercent >= 95;
    }

    return false;
  }

  /**
   * Gets the start position for resuming playback.
   * Returns null if no valid progress exists.
   *
   * @param {string} mediaId - The media ID
   * @param {string} mediaType - The type of media (movie/tv)
   * @returns {number|null} Start time in seconds, or null
   */
  getResumePosition(mediaId, mediaType) {
    const progressData = this.getValidatedProgress(mediaId, mediaType);

    if (
      progressData.isValid &&
      progressData.progress &&
      progressData.duration
    ) {
      // Don't resume if already watched or if less than 10 seconds remaining
      if (
        progressData.watched ||
        progressData.duration - progressData.progress < 10000
      ) {
        return null;
      }

      // Return position in seconds
      return progressData.progress / 1000;
    }

    return null;
  }
}

// Export singleton instance
const watchProgressUtils = new WatchProgressUtils();

export default watchProgressUtils;
